import { BIRD_FACTS } from '../data/birdFacts';
import { buffProduct, buffRemainingMs } from '../core/buffs';
import { farmTotals } from '../core/economy';
import { formatCount, formatDuration, formatMoney, formatRate } from '../core/format';
import { nextGoal, type GoalFocus } from '../core/goals';
import { countHens, countRoosters } from '../core/state';
import { game } from './controller';
import { byId, clear, h } from './dom';

/** Header stats, coach line, flock tabs, active-effect chips and the bird-fact ticker. */

interface StatEl { root: HTMLElement; value: HTMLElement }
const stats: Record<string, StatEl> = {};
let coachText: HTMLElement;
let coachBar: HTMLElement;
let lastGoal = '';
let onCoachClick: (f: GoalFocus) => void = () => {};
let lastTabsKey = '';

function stat(key: string, label: string, cls = ''): void {
  const value = h('span', { class: 'value' }, '—');
  const root = h('div', { class: `stat ${cls}`, title: label }, h('span', { class: 'label' }, label), value);
  byId('stats').append(root);
  stats[key] = { root, value };
}

export function initHud(coachClick: (f: GoalFocus) => void): void {
  onCoachClick = coachClick;
  stat('money', 'Balance', 'money');
  stat('income', 'Income');
  stat('laying', 'Laying');
  stat('gather', 'Gathering');
  stat('hens', 'Hens');
  stat('roosters', 'Roosters');
  const coach = byId('coach');
  coachText = h('span', { class: 'text' });
  coachBar = h('span');
  coach.append(h('span', { class: 'arrow' }, '👉'), coachText, h('div', { class: 'bar' }, coachBar));
  coach.addEventListener('click', () => onCoachClick(nextGoal(game.state, game.ctx()).focus));
  coach.style.cursor = 'pointer';
  rotateFact();
  window.setInterval(rotateFact, 20_000);
}

function rotateFact(): void {
  byId('ticker').textContent = BIRD_FACTS[Math.floor(Math.random() * BIRD_FACTS.length)] ?? '';
}

export function updateHud(): void {
  const s = game.state;
  const c = game.ctx();
  const t = farmTotals(s, c);
  stats.money!.value.textContent = formatMoney(s.money);
  stats.income!.value.textContent = `${formatRate(t.passiveIncome)}/s`;
  stats.income!.root.title = 'Money per second from auto-gathering (clicking earns extra). This is also what you earn while away.';
  stats.laying!.value.textContent = `${t.layRate.toFixed(1)} eggs/s`;
  stats.gather!.value.textContent = t.gatherRate > 0 ? `${Math.round(t.efficiency * 100)}%` : 'none';
  stats.gather!.root.title = t.gatherRate > 0 ? `Gatherers collect ${t.gatherRate.toFixed(1)} eggs/s of the ${t.layRate.toFixed(1)} laid.` : 'No auto-gathering yet — buy an Egg Basket in the Farm tab.';
  stats.gather!.root.classList.toggle('warn', t.gatherRate > 0 && t.efficiency < 0.6);
  stats.hens!.value.textContent = formatCount(countHens(s));
  stats.roosters!.value.textContent = `${countRoosters(s)}/10`;
  byId('farm-name').textContent = s.farmName ? `· ${s.farmName}` : '';

  const coach = byId('coach');
  coach.style.display = game.prefs.coach ? '' : 'none';
  const goal = nextGoal(s, c);
  if (goal.text !== lastGoal) {
    if (lastGoal) {
      coach.classList.remove('pulse');
      void coach.offsetWidth;
      coach.classList.add('pulse');
    }
    lastGoal = goal.text;
    coachText.textContent = goal.text;
  }
  coachBar.style.width = `${Math.max(0, Math.min(1, goal.progress ?? 0)) * 100}%`;

  updateFlockTabs();
  updateEffects();
}

function updateFlockTabs(): void {
  const s = game.state;
  const key = s.flocks.map((f) => `${f.id}:${f.name}:${f.boxes.some((b) => b.eggs >= 4) ? 1 : 0}`).join('|') + `|${s.viewFlock}`;
  if (key === lastTabsKey) return;
  lastTabsKey = key;
  const nav = byId('flock-tabs');
  clear(nav);
  nav.style.display = s.flocks.length > 1 ? '' : 'none';
  for (const f of s.flocks) {
    const busy = f.id !== s.viewFlock && s.events.some((e) => e.flockId === f.id && (e.kind === 'broody' || e.kind === 'broodEgg'));
    nav.append(h('button', {
      class: `flock-tab ${f.id === s.viewFlock ? 'active' : ''}`,
      type: 'button',
      onclick: () => { game.setView(f.id); lastTabsKey = ''; },
    }, f.name, busy ? h('span', { class: 'dot', title: 'Something needs attention here' }) : null));
  }
}

function updateEffects(): void {
  const s = game.state;
  const now = s.time;
  const chips: string[] = [];
  const lay = buffProduct(s.buffs.lay, now);
  if (lay > 1) chips.push(`🥚 Laying ×${lay.toFixed(1)} — ${formatDuration(buffRemainingMs(s.buffs.lay, now))}`);
  const click = buffProduct(s.buffs.click, now);
  if (click > 1) chips.push(`👆 Clicks ×${click.toFixed(1)} — ${formatDuration(buffRemainingMs(s.buffs.click, now))}`);
  const auto = buffProduct(s.buffs.auto, now);
  if (auto > 1) chips.push(`🧺 Gathered eggs ×${auto.toFixed(1)} — ${formatDuration(buffRemainingMs(s.buffs.auto, now))}`);
  const gather = buffProduct(s.buffs.gather, now);
  if (gather > 1) chips.push(`⚙️ Gathering ×${gather.toFixed(1)} — ${formatDuration(buffRemainingMs(s.buffs.gather, now))}`);
  const flock = game.viewFlock();
  const crowMult = buffProduct(flock.crowBuffs, now);
  if (crowMult > 1) chips.push(`📣 Crow ×${crowMult.toFixed(1)} laying — ${formatDuration(buffRemainingMs(flock.crowBuffs, now))}`);
  for (const cb of s.clutch) chips.push(`🍀 Next ${cb.remaining} clicked eggs ×${cb.mult.toFixed(0)}`);
  if (s.combo.tier > 0) chips.push(`🔥 Combo ×${(1 + s.combo.tier * 0.1).toFixed(1)} clicks`);
  if (now < s.debuffUntil) chips.push(`🤢 Rotten smell: egg value −15% — ${formatDuration(s.debuffUntil - now)}`);
  if (s.vouchers > 0) chips.push(`🐣 ${s.vouchers} cockerel voucher${s.vouchers > 1 ? 's' : ''}: next rooster −${s.vouchers * 25}%`);
  const box = byId('effects');
  const html = chips.map((c) => `<div class="effect-chip">${c}</div>`).join('');
  if (box.innerHTML !== html) box.innerHTML = html;
}
